import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Terminal, ChevronDown, ChevronRight, RefreshCw, TrendingDown, TrendingUp,
  BarChart3, Filter, Globe,
} from "lucide-react";

// Phase 25.3a — Funnel Analytics Dashboard.
// Shows step-by-step conversion rates between every commercial surface and
// the pipeline. Founder gets an at-a-glance read on where the funnel leaks.

const WINDOW_OPTIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export default function FunnelAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/funnel-analytics?days=${d}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load funnel");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const funnel = data?.funnel || [];
  const stages = data?.pipeline_by_stage || {};

  return (
    <div className="min-h-screen bg-black text-white" data-testid="funnel-analytics-page">
      <header className="border-b border-zinc-900 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/app/today" className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-[#FFB000]" />
              <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            </Link>
            <div className="hidden sm:block w-px h-6 bg-zinc-800" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// ADMIN · FUNNEL ANALYTICS</div>
              <h1 className="font-heading font-black text-lg tracking-tight">Sales Funnel · Last {days}d</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {WINDOW_OPTIONS.map(o => (
              <button
                key={o.days}
                onClick={() => setDays(o.days)}
                data-testid={`funnel-window-${o.days}`}
                className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border ${
                  days === o.days
                    ? "border-[#FFB000] bg-[#FFB000]/10 text-[#FFB000]"
                    : "border-zinc-800 text-zinc-400 hover:text-white"
                }`}
              >
                {o.label}
              </button>
            ))}
            <button
              onClick={() => load()}
              data-testid="funnel-refresh-btn"
              className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              REFRESH
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {loading && !data && (
          <div className="text-center py-20 font-mono text-[11px] uppercase tracking-widest text-zinc-600">LOADING FUNNEL…</div>
        )}

        {data && (
          <>
            {/* The funnel ladder */}
            <div className="border border-zinc-900 bg-zinc-950/40 mb-6">
              <div className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// CONVERSION LADDER</div>
                <span className="font-mono text-[10px] text-zinc-600">Window: {days} days</span>
              </div>
              <div className="divide-y divide-zinc-900" data-testid="funnel-ladder">
                {funnel.map((step, i) => {
                  const widthPct = funnel[0]?.count ? Math.max(8, (step.count / funnel[0].count) * 100) : 100;
                  const isLast = i === funnel.length - 1;
                  return (
                    <FunnelRow
                      key={i}
                      step={step.step}
                      count={step.count}
                      conversion={step.conversion_pct}
                      widthPct={widthPct}
                      accentByStep={i}
                      testid={`funnel-step-${i}`}
                      showDropoff={!isLast && i > 0}
                      previousCount={i > 0 ? funnel[i - 1].count : 0}
                    />
                  );
                })}
              </div>
            </div>

            {/* Pipeline by stage */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="lg:col-span-2 border border-zinc-900 bg-zinc-950/40">
                <div className="border-b border-zinc-900 px-5 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// PIPELINE BY STAGE (last {days}d)</div>
                </div>
                <div className="p-5 space-y-2">
                  {Object.entries(stages).map(([stage, count]) => (
                    <PipelineStageRow key={stage} stage={stage} count={count} total={data.pipeline_total || 1} />
                  ))}
                </div>
              </div>

              <div className="border border-zinc-900 bg-zinc-950/40">
                <div className="border-b border-zinc-900 px-5 py-3 flex items-center gap-2">
                  <BarChart3 className="w-3.5 h-3.5 text-[#FFB000]" />
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// ROI CALC · BY INDUSTRY</div>
                </div>
                <div className="p-5">
                  {(data.roi_by_industry || []).length === 0 && (
                    <div className="text-center py-6 font-mono text-[10px] text-zinc-700">No sessions yet.</div>
                  )}
                  <div className="space-y-3">
                    {(data.roi_by_industry || []).map((ind, i) => (
                      <div key={i} data-testid={`funnel-roi-ind-${ind.industry}`} className="flex items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-sm text-white truncate">{ind.industry}</div>
                          <div className="font-mono text-[9px] text-zinc-600">
                            {ind.sessions} sessions · ${(ind.total_modeled_impact_usd / 1e6).toFixed(1)}M modeled
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`font-heading font-black text-lg tracking-tighter ${ind.conversion_pct >= 10 ? "text-[#00E676]" : ind.conversion_pct >= 3 ? "text-[#FFB000]" : "text-zinc-400"}`}>
                            {ind.conversion_pct}%
                          </div>
                          <div className="font-mono text-[9px] text-zinc-700">→ pilot</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Insights footer */}
            <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.02] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-2">// WHERE TO FOCUS</div>
              <FunnelInsights funnel={funnel} stages={stages} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FunnelRow({ step, count, conversion, widthPct, accentByStep, testid, showDropoff, previousCount }) {
  const accents = ["#00E676", "#FFB000", "#FFB000", "#FFB000", "#FF6B47", "#00E676", "#00E676"];
  const accent = accents[accentByStep % accents.length];
  return (
    <div className="px-5 py-4 grid grid-cols-12 gap-3 items-center" data-testid={testid}>
      <div className="col-span-5 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
        {step}
      </div>
      <div className="col-span-5 relative h-6 bg-zinc-900 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 transition-all"
          style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, ${accent}66, ${accent}99)` }}
        />
        <div className="absolute inset-0 flex items-center px-2 font-heading font-black text-sm tracking-tighter text-white">
          {count.toLocaleString()}
        </div>
      </div>
      <div className="col-span-2 text-right">
        {conversion != null && conversion >= 0 && (
          <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: accent }}>
            {conversion}%
          </div>
        )}
        {showDropoff && (
          <div className="font-mono text-[9px] text-zinc-600">
            -{(previousCount - count).toLocaleString()} dropped
          </div>
        )}
      </div>
    </div>
  );
}

function PipelineStageRow({ stage, count, total }) {
  const widthPct = Math.max(5, (count / total) * 100);
  const colors = {
    NEW: "#71717a", QUALIFIED: "#FFB000", DEMO_BOOKED: "#FFB000", IN_PILOT: "#00E676",
    NEGOTIATING: "#00E676", WON: "#00E676", LOST: "#FF6B47",
  };
  const color = colors[stage] || "#71717a";
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`funnel-stage-${stage}`}>
      <div className="col-span-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{stage.replace("_", " ")}</div>
      <div className="col-span-7 relative h-5 bg-zinc-900 overflow-hidden">
        <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${widthPct}%`, background: color + "AA" }} />
      </div>
      <div className="col-span-2 text-right font-heading font-bold text-sm tracking-tighter" style={{ color }}>{count}</div>
    </div>
  );
}

function FunnelInsights({ funnel, stages }) {
  // Compute the biggest drop-off
  let biggestDrop = null;
  for (let i = 1; i < funnel.length; i++) {
    const prev = funnel[i - 1];
    const cur = funnel[i];
    if (prev.count === 0) continue;
    const dropPct = 100 - ((cur.count / prev.count) * 100);
    if (!biggestDrop || dropPct > biggestDrop.dropPct) {
      biggestDrop = { from: prev.step, to: cur.step, dropPct, lost: prev.count - cur.count };
    }
  }
  const wonRate = stages.WON ? Math.round(100 * stages.WON / Object.values(stages).reduce((a, b) => a + b, 0)) : 0;
  const insights = [];
  if (biggestDrop) {
    insights.push({
      icon: TrendingDown,
      color: "#FF6B47",
      text: `Biggest drop-off: ${biggestDrop.from} → ${biggestDrop.to} (-${biggestDrop.dropPct.toFixed(0)}%, ${biggestDrop.lost.toLocaleString()} lost)`,
    });
  }
  if (wonRate >= 20) {
    insights.push({ icon: TrendingUp, color: "#00E676", text: `Strong WON rate: ${wonRate}% of pipeline closes. Keep doing what you're doing.` });
  } else if (wonRate < 5 && stages.WON !== undefined && (Object.values(stages).reduce((a, b) => a + b, 0) > 5)) {
    insights.push({ icon: TrendingDown, color: "#FF6B47", text: `WON rate only ${wonRate}% — review your demo → close motion.` });
  }
  if (funnel.length > 0 && funnel[0].count === 0) {
    insights.push({ icon: Globe, color: "#FFB000", text: "Zero traffic in window — focus on outbound or paid distribution to fill the top of funnel." });
  }
  if (insights.length === 0) {
    insights.push({ icon: BarChart3, color: "#FFB000", text: "Funnel looks healthy. Increase top-of-funnel volume to compound." });
  }
  return (
    <ul className="space-y-2">
      {insights.map((ins, i) => {
        const Icon = ins.icon;
        return (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-200" data-testid={`funnel-insight-${i}`}>
            <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ins.color }} />
            <span>{ins.text}</span>
          </li>
        );
      })}
    </ul>
  );
}
